/**
 * دو عددی که فقط سرور می‌تونه بدونشون: امتیازِ محبوبیت و زمانِ افزودن.
 *
 * محبوبیت از پنج جزء ساخته می‌شه (منابعِ لیست‌کننده، سیگنال‌های ساخته‌شده،
 * نشان‌شدن‌ها، بازدیدِ صفحه‌ی جزئیات، کلیکِ لینک) و وزنِ هر جزء توی جدولِ
 * `popularity_weights` می‌شینه - عوض کردنشون یه UPDATE است. کلِ فرمول توی
 * مایگریشن 0049 توضیح داده شده و همون‌جا هم حساب می‌شه.
 *
 * چرا جدا از eventRepo: این عددها با هر نشان کردن و هر بازدید عوض می‌شن، ولی
 * `content_hash`ِ رخداد دست‌نخورده می‌مونه. اگه قاطیِ خودِ رکورد می‌اومدن،
 * یا کشِ آفلاین کهنه می‌شد یا با هر تغییرِ کوچیک کلِ فهرست (با عکس‌ها) دوباره
 * دانلود می‌شد. این کوئری فقط شناسه و دو عدده.
 */
import { supabase } from '../lib/supabase';
import { isDemoMode } from '../lib/demo';
import type { MyEventStats } from '../lib/myEvents';

/* eslint-disable @typescript-eslint/no-explicit-any */

export interface ItemMeta {
  id: string;
  /** امتیازِ محبوبیت. بالاتر یعنی محبوب‌تر؛ کاربر هیچ‌وقت خودِ عدد رو نمی‌بینه. */
  score: number;
  /** لحظه‌ی افزوده شدن به دیتابیس (ISO). */
  createdAt: string | null;
}

export const popularityRepo = {
  /**
   * امتیاز و زمانِ افزودنِ همه‌ی رخدادها و مکان‌های منتشرشده.
   *
   * عمداً بدونِ فیلترِ kind یا city: خروجی برای هر ردیف چند بایته و یه
   * کوئریِ واحد از دو تا کمتر هزینه داره.
   */
  async metas(): Promise<ItemMeta[]> {
    if (isDemoMode) return [];
    const { data, error } = await supabase
      .from('event_popularity')
      .select('event_id, score, created_at');
    if (error) throw error;
    return (data ?? []).map((r: any) => ({
      id: String(r.event_id),
      score: Number(r.score) || 0,
      createdAt: r.created_at ?? null,
    }));
  },

  /**
   * یکی به شمارنده اضافه کن - بازدیدِ صفحه‌ی جزئیات یا کلیکِ لینک.
   *
   * جواب هیچ‌جا خونده نمی‌شه و خطاش هم بالا نمی‌ره (نگاه کن به صداکننده‌ها):
   * شمردن کارِ جانبیه و نباید صفحه‌ای که کاربر باز کرده رو خراب کنه.
   */
  async bump(eventId: string, kind: 'detail_view' | 'link_click'): Promise<void> {
    if (isDemoMode) return;
    const { error } = await supabase.rpc('bump_event_counter', {
      p_event_id: eventId, p_kind: kind,
    });
    if (error) throw error;
  },

  /**
   * «کارتِ این‌ها جلوی چشم اومد» — دسته‌ای، برای کلِ چیزی که توی یه اسکرول
   * دیده شد (مایگریشن 0077).
   *
   * چرا دسته‌ای: تک‌تک یعنی ده تا درخواست برای یه بالا و پایینِ ساده. و چرا
   * اصلاً: بدونِ این عدد، «چند نفر بازش کردن» مخرج نداره و صاحبِ رخداد
   * نمی‌فهمه کارتش کار می‌کنه یا نه.
   *
   * هر آدم روی هر رخداد فقط یه بار شمرده می‌شه — و اون یکتاسازی سمتِ
   * دیتابیسه (کلیدِ اصلیِ event_viewers)، نه این‌جا. مجموعه‌ی توی صداکننده
   * فقط برای کم کردنِ رفت‌وبرگشتِ بی‌فایده‌ست.
   *
   * مثلِ `bump`، جوابش خونده نمی‌شه و خطاش هم بالا نمی‌ره برای صداکننده‌ها:
   * شمردن نباید فهرستی که کاربر داره می‌بینه رو خراب کنه.
   */
  async bumpImpressions(eventIds: string[]): Promise<void> {
    if (isDemoMode || eventIds.length === 0) return;
    const { error } = await supabase.rpc('bump_event_impressions', { p_event_ids: eventIds });
    if (error) throw error;
  },

  /**
   * سه شمارشِ یکتا برای رخدادهای خودِ کاربر — کلید بر حسبِ **ریشه**ی زنجیره‌ی
   * نسخه‌هاست، نه ردیف (مایگریشن 0077).
   *
   * `event_viewers` هیچ سیاستِ خواندنی نداره (مایگریشن 0070، عمداً: اسم
   * می‌بره چه کسی چی رو باز کرده)، پس این عددها فقط از راهِ همون تابعِ
   * security definer در می‌آن و فقط برای رخدادی که خودت فرستادیش.
   */
  async myStats(rootIds: string[]): Promise<Map<string, MyEventStats>> {
    const out = new Map<string, MyEventStats>();
    if (isDemoMode || rootIds.length === 0) return out;
    const { data, error } = await supabase.rpc('my_event_stats', { p_root_ids: rootIds });
    if (error) throw error;
    for (const r of (data ?? []) as any[]) {
      out.set(String(r.root_id), {
        impressions: Number(r.impressions) || 0,
        detailViews: Number(r.detail_views) || 0,
        linkClicks: Number(r.link_clicks) || 0,
      });
    }
    return out;
  },
};
